import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthorStatusBadge } from '../../utils/authorStatus';

interface NoteImpact {
  totalViews: number;
  uniqueDisplaysCount: number;
  displayLocationsSummary: Array<{city: string; state: string}>;
  inspiredNotesCount: number;
  inspirationChainLength: number;
  totalHearts: number;
  totalQrScans: number;
}

interface Note {
  _id: string;
  note: string;
  createdAt: string;
  updatedAt?: string;
  contributorAliasName?: string;
  location_city?: string;
  location_state?: string;
  heart_count: number;
  moderation?: {
    /** Raw status for moderators/admins; 'approved' or 'pending' for the author. */
    status: 'pending' | 'ai-approved' | 'moderator-approved' | 'rejected' | 'approved';
    moderatorComment?: string;
    quality_score?: number;
    history?: Array<{timestamp: string; originalNote: string}>;
  };
  display?: {
    viewCount: number;
  };
  flags?: {
    count: number;
  };
  categories?: string[];
  tags?: string[];
}

interface NoteDetailProps {
  note: Note;
  impact?: NoteImpact;
  isLoading?: boolean;
  error?: string;
  isOwner?: boolean;
  isAdmin?: boolean;
  relatedNotes?: Note[];
  onHeartClick?: () => void;
  onFlagClick?: () => void;
  isHearted?: boolean;
  onShareClick?: () => void;
  onEditClick?: () => void;
}

const NoteDetail: React.FC<NoteDetailProps> = ({
  note,
  impact,
  isLoading = false,
  error,
  isOwner = false,
  isAdmin = false,
  relatedNotes = [],
  onHeartClick,
  onFlagClick,
  isHearted = false,
  onShareClick,
  onEditClick
}) => {
  const [activeTab, setActiveTab] = useState<'details' | 'impact'>('details');
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [flagReason, setFlagReason] = useState('');
  
  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  
  // Render note status badge
  const renderStatusBadge = (status?: string) => {
    // Don't show status badge if user doesn't have permission to see moderation data
    if (!status || status === 'Unknown') {
      return null; // Hide status badge for users without moderation permissions
    }

    // Anyone but a moderator/admin (i.e. the author) sees Approved or Pending only
    if (!isAdmin) {
      return <AuthorStatusBadge status={status} />;
    }

    let badgeClass = 'bg-secondary';
    let label = 'Unknown';
    
    switch (status) {
      case 'pending':
        badgeClass = 'bg-warning text-dark';
        label = 'Pending';
        break;
      case 'ai-approved':
        badgeClass = 'bg-info text-dark';
        label = 'AI Approved';
        break;
      case 'moderator-approved':
        badgeClass = 'bg-success';
        label = 'Approved';
        break;
      case 'rejected':
        badgeClass = 'bg-danger';
        label = 'Rejected';
        break;
    }
    
    return <span className={`badge ${badgeClass}`}>{label}</span>;
  };
  
  // Handle flag modal submission
  const handleFlagSubmit = () => {
    if (onFlagClick && flagReason.trim()) {
      onFlagClick();
      setFlagModalOpen(false);
      setFlagReason('');
    }
  };
  
  if (isLoading) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading note details...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }
  
  return (
    <div className="note-detail">
      {/* Note Content Card */}
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div>
            {renderStatusBadge(note.moderation?.status)}
            <div className="note-meta d-flex flex-wrap gap-3 mb-3">
              <span className="text-muted">
                <i className="fas fa-clock me-1"></i>
                {formatDate(note.createdAt)}
              </span>
            </div>
          </div>
          {/* Action buttons */}
          <div className="d-flex gap-2">
            {onHeartClick && (
              <button 
                className={`btn btn-sm ${isHearted ? 'btn-danger' : 'btn-outline-danger'}`}
                onClick={onHeartClick}
                aria-label={isHearted ? 'Remove heart' : 'Heart this note'}
              >
                <i className={`fas fa-heart${isHearted ? '' : '-empty'} me-1`}></i>
                {note.heart_count}
              </button>
            )}
            
            {onShareClick && (
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={onShareClick}
                aria-label="Share note"
              >
                <i className="fas fa-share me-1"></i>
                Share
              </button>
            )}

            {/* TODO: Implement flag functionality for public notes - currently hidden for non-admins */}
            {onFlagClick && !isOwner && isAdmin && (
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setFlagModalOpen(true)}
                aria-label="Flag inappropriate content"
              >
                <i className="fas fa-flag me-1"></i>
                Flag
              </button>
            )}
            
            {isOwner && onEditClick && (
              <button 
                className="btn btn-sm btn-outline-secondary"
                onClick={onEditClick}
                aria-label="Edit note"
              >
                <i className="fas fa-pencil-alt me-1"></i>
                Edit
              </button>
            )}
          </div>
        </div>
        
        <div className="card-body">
          {/* Contributor Info */}
          <div className="contributor-info mb-3">
            <h5>From: {note.contributorAliasName || 'Anonymous'}</h5>
            {(note.location_city || note.location_state) && (
              <div className="location-info mb-3">
                <small className="text-muted">
                  <i className="fas fa-map-marker-alt me-1"></i>
                  {note.location_city}{note.location_city && note.location_state ? ', ' : ''}{note.location_state}
                </small>
              </div>
            )}
          </div>
          
          {/* Note Content */}
          <div className="note-content mb-4">
            <p className="lead">{note.note}</p>
          </div>
          
          {/* Categories & Tags */}
          {note.categories && note.categories.length > 0 && (
            <div className="categories mb-3">
              <small className="text-muted me-2">Categories:</small>
              {note.categories.map((category, index) => (
                <span key={index} className="badge bg-primary me-1">
                  {category}
                </span>
              ))}
            </div>
          )}
          
          {note.tags && note.tags.length > 0 && (
            <div className="tags mb-3">
              <small className="text-muted me-2">Tags:</small>
              {note.tags.map((tag, index) => (
                <span key={index} className="badge bg-secondary me-1">
                  {tag}
                </span>
              ))}
            </div>
          )}
          
          {/* Moderator Comments (moderators/admins only; never shown to the author) */}
          {isAdmin && note.moderation?.moderatorComment && (
            <div className="alert alert-info mt-3">
              <h6>Moderator Comment:</h6>
              <p className="mb-0">{note.moderation.moderatorComment}</p>
            </div>
          )}
        </div>
      </div>

      {/* TODO: Complete implementation of Details and Impact tabs with full data */}
      {/* Tabs for Details/Impact - Only visible to admins/moderators */}
      {isAdmin && (
        <>
          <ul className="nav nav-tabs mb-4">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'details' ? 'active' : ''}`}
                onClick={() => setActiveTab('details')}
              >
                Details
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'impact' ? 'active' : ''}`}
                onClick={() => setActiveTab('impact')}
              >
                Impact
              </button>
            </li>
          </ul>

          {/* Details Tab Content */}
          {activeTab === 'details' && (
        <div className="details-tab">
          <div className="row">
            <div className="col-md-6 mb-4">
              <div className="card h-100">
                <div className="card-header">
                  <h5 className="card-title mb-0">Note Information</h5>
                </div>
                <div className="card-body">
                  <ul className="list-group list-group-flush">
                    <li className="list-group-item d-flex justify-content-between">
                      <span>Created</span>
                      <span>{formatDate(note.createdAt)}</span>
                    </li>
                    {note.updatedAt && note.updatedAt !== note.createdAt && (
                      <li className="list-group-item d-flex justify-content-between">
                        <span>Last Updated</span>
                        <span>{formatDate(note.updatedAt)}</span>
                      </li>
                    )}
                    <li className="list-group-item d-flex justify-content-between">
                      <span>Status</span>
                      <span>{renderStatusBadge(note.moderation?.status)}</span>
                    </li>
                    {note.moderation?.quality_score !== undefined && (
                      <li className="list-group-item d-flex justify-content-between">
                        <span>Quality Score</span>
                        <span>{note.moderation.quality_score}/100</span>
                      </li>
                    )}
                    <li className="list-group-item d-flex justify-content-between">
                      <span>Hearts</span>
                      <span>{note.heart_count}</span>
                    </li>
                    {note.display?.viewCount !== undefined && (
                      <li className="list-group-item d-flex justify-content-between">
                        <span>Views</span>
                        <span>{note.display.viewCount}</span>
                      </li>
                    )}
                    {note.flags?.count !== undefined && (
                      <li className="list-group-item d-flex justify-content-between">
                        <span>Flags</span>
                        <span>{note.flags.count}</span>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Related Notes Section */}
            <div className="col-md-6 mb-4">
              <div className="card h-100">
                <div className="card-header">
                  <h5 className="card-title mb-0">Related Notes</h5>
                </div>
                <div className="card-body">
                  {relatedNotes.length > 0 ? (
                    <div className="list-group">
                      {relatedNotes.slice(0, 5).map(relatedNote => (
                        <Link 
                          key={relatedNote._id}
                          to={`/my-notes/${relatedNote._id}`}
                          className="list-group-item list-group-item-action"
                        >
                          <div className="d-flex justify-content-between">
                            <h6 className="mb-1">{relatedNote.contributorAliasName || 'Anonymous'}</h6>
                            <small>{formatDate(relatedNote.createdAt)}</small>
                          </div>
                          <p className="mb-1">{relatedNote.note.length > 100 
                            ? `${relatedNote.note.substring(0, 100)}...` 
                            : relatedNote.note}
                          </p>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted">No related notes found.</p>
                  )}
                </div>
                {relatedNotes.length > 5 && (
                  <div className="card-footer">
                    <button className="btn btn-sm btn-outline-primary">View All Related Notes</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Impact Tab Content */}
      {activeTab === 'impact' && impact && (
        <div className="impact-tab">
          <div className="row">
            <div className="col-md-6 mb-4">
              <div className="card h-100">
                <div className="card-header">
                  <h5 className="card-title mb-0">Impact Metrics</h5>
                </div>
                <div className="card-body">
                  <ul className="list-group list-group-flush">
                    <li className="list-group-item d-flex justify-content-between">
                      <span>Total Views</span>
                      <span>{impact.totalViews}</span>
                    </li>
                    <li className="list-group-item d-flex justify-content-between">
                      <span>Unique Displays</span>
                      <span>{impact.uniqueDisplaysCount}</span>
                    </li>
                    <li className="list-group-item d-flex justify-content-between">
                      <span>Inspired Notes</span>
                      <span>{impact.inspiredNotesCount}</span>
                    </li>
                    <li className="list-group-item d-flex justify-content-between">
                      <span>Inspiration Chain Length</span>
                      <span>{impact.inspirationChainLength}</span>
                    </li>
                    <li className="list-group-item d-flex justify-content-between">
                      <span>Total Hearts</span>
                      <span>{impact.totalHearts}</span>
                    </li>
                    <li className="list-group-item d-flex justify-content-between">
                      <span>QR Code Scans</span>
                      <span>{impact.totalQrScans}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="col-md-6 mb-4">
              <div className="card h-100">
                <div className="card-header">
                  <h5 className="card-title mb-0">Display Locations</h5>
                </div>
                <div className="card-body">
                  {impact.displayLocationsSummary.length > 0 ? (
                    <div>
                      <div className="mb-3">
                        <h6>This note has been displayed in {impact.displayLocationsSummary.length} locations:</h6>
                      </div>
                      <div className="location-list">
                        {impact.displayLocationsSummary.map((location, index) => (
                          <div key={index} className="badge bg-light text-dark me-2 mb-2 p-2">
                            <i className="fas fa-map-marker-alt me-1"></i>
                            {location.city}, {location.state}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted">No display location data available.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
        </>
      )}

      {/* Flag Modal */}
      {flagModalOpen && (
        <div className="modal d-block" tabIndex={-1} style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Flag Inappropriate Content</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setFlagModalOpen(false)}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <p>Please explain why you're flagging this note:</p>
                <textarea
                  className="form-control"
                  rows={4}
                  value={flagReason}
                  onChange={(e) => setFlagReason(e.target.value)}
                  placeholder="Enter reason for flagging..."
                  required
                ></textarea>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setFlagModalOpen(false)}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger"
                  onClick={handleFlagSubmit}
                  disabled={!flagReason.trim()}
                >
                  Submit Flag
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NoteDetail; 